import React, { useMemo, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert as MUIAlert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Tooltip,
  Grid,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMutation, useQuery } from "@apollo/client";
import { useSelectedBusiness } from "../../../../platform/redux/store";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BusinessIcon from "@mui/icons-material/Business";
// GraphQL queries
import {
  GET_CARE_PRICING_CATALOG,
  GET_AGENT_BOOKING_TYPES,
  GET_OPEN_BEDS,
  GET_REFERRAL_LEADS,
} from "../../../../platform/graphql/queries/care";
import { GET_BOOKING_PROVIDER_CONNECTION } from "../../../../platform/graphql/queries/generic";
import { buildBookingProviderAvailabilityUrl } from "../../../../modules/booking-provider";

// GraphQL mutations
import { SEND_EMAIL_MUTATION } from "../../../../platform/graphql/mutations";
import {
  CHANGE_REFERRAL_LEAD_STATUS,
  CONFIRM_REQUEST_SETTLEMENT,
  PAY_REFERRAL_INSTALLMENT,
  RESERVE_REQUEST_BEDS,
} from "../../../../platform/graphql/mutations/care";

// UI modules
import VacateBedsDialog from "../../../../platform/careComponents/vacateBedsDialog";
import CareProvider from "../../../../platform/careComponents/pages/register/careProvider";
import RoomsBedsMetaSection from "../../../staffCaregiverHiring/careJobs/hooks/useRoomAvailability";
import PayInvoiceDialog from "../components/payInvoiceDialog";
import { getPricingFlagsFromExtra } from "../../../../platform/careComponents/helper/referralHelpers";
import useBusinessFromStore from "../../../../platform/careComponents/hooks/useBusinessFromStore";
import IncomingReferralsSection from "../components/IncomingReferralsSection";
import { BedMultiSettleDialog } from "./bedMultiSettleDialog";
import { JobPostDialog } from "./jobPostingDialog";

/* ------------------------------------------------------------------
 * Mini invoice
 * ------------------------------------------------------------------ */
type MiniInvoice = { id: string; amount: number };

const FALLBACK_MIN_SELF_SERVE = 2500; // per bed
const FALLBACK_MIN_CONCIERGE = 3000; // per bed
const FALLBACK_RUSH_ADDER = 500;     // per bed

function SummaryMetricCard({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: React.ReactNode;
  tone?: "default" | "warm" | "cool";
}) {
  const theme = useTheme();
  const surfaces = theme.customSurfaces || {};
  const tones = {
    default: {
      background: surfaces.elevatedPanel || theme.palette.background.paper,
      border: surfaces.border || alpha(theme.palette.divider, 0.5),
    },
    warm: {
      background: surfaces.elevatedPanel || theme.palette.background.paper,
      border: surfaces.border || alpha(theme.palette.divider, 0.5),
    },
    cool: {
      background: surfaces.elevatedPanel || theme.palette.background.paper,
      border: surfaces.border || alpha(theme.palette.divider, 0.5),
    },
  }[tone];

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3.5,
        border: `1px solid ${tones.border}`,
        background: tones.background,
        minWidth: { xs: "100%", sm: 160 },
        color: theme.palette.text.primary,
        boxShadow: "0 10px 22px rgba(9, 20, 31, 0.05)",
      }}
    >
      <Typography
        variant="overline"
        sx={{
          display: "block",
          color: theme.palette.text.secondary,
          letterSpacing: "0.08em",
          lineHeight: 1.2,
          mb: 0.6,
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ lineHeight: 1.1, color: theme.palette.text.primary, textShadow: "none" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function DashboardPanel({
  title,
  actions,
  children,
  collapsible = false,
  defaultExpanded = true,
  fillHeight = true,
}: {
  title: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  fillHeight?: boolean;
}) {
  const theme = useTheme();
  const surfaces = theme.customSurfaces || {};
  const header = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      spacing={1}
      sx={{ mb: collapsible ? 0 : 1.5 }}
    >
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      {actions ? (
        <Box onClick={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()}>
          {actions}
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: fillHeight ? "100%" : "auto",
        borderRadius: 5,
        borderColor: surfaces.border || "rgba(103, 114, 126, 0.22)",
        background: surfaces.elevatedPanel || theme.palette.background.paper,
        boxShadow: "0 22px 44px rgba(15, 31, 46, 0.08)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, lg: 3 } }}>
        {collapsible ? (
          <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            elevation={0}
            square
            sx={{
              bgcolor: "transparent",
              "&:before": { display: "none" },
              "&.Mui-expanded": { mt: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 0,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.5 },
              }}
            >
              {header}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1, pb: 0 }}>
              {children}
            </AccordionDetails>
          </Accordion>
        ) : (
          <>
            {header}
            {children}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* --------------------------- Email helpers ----------------------- */
function buildCancelApologyEmail(params: {
  businessName: string;
  referrerName?: string | null;
  inquiryKind?: string | null;
}) {
  const { businessName, referrerName, inquiryKind } = params;
  const who = referrerName ? `Hi ${referrerName},` : "Hello,";
  const subject = `We're sorry — your request was not placed at ${businessName}`;
  const text = `${who}

We wanted to let you know that your ${inquiryKind ?? "care"
    } request was not placed at ${businessName}.
If this was a mistake or you still need assistance, reply to this email and we’ll help right away.

— ${businessName} Care Team
`;
  const html = `
  <div style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; line-height:1.5;">
    <p>${who}</p>
    <p>We wanted to let you know that your <strong>${inquiryKind ?? "care"
    }</strong> request was <strong>not placed</strong> at <strong>${businessName}</strong>.</p>
    <p>If this was a mistake or you still need assistance, reply to this email and we’ll help right away.</p>
    <p>— ${businessName} Care Team</p>
  </div>`.trim();
  return { subject, text, html };
}

function buildReopenCorrectionEmail(params: {
  businessName: string;
  referrerName?: string | null;
  inquiryKind?: string | null;
}) {
  const { businessName, referrerName, inquiryKind } = params;
  const who = referrerName ? `Hi ${referrerName},` : "Hello,";
  const subject = `Correction — your request at ${businessName} is active`;
  const text = `${who}

A quick correction: your ${inquiryKind ?? "care"
    } request at ${businessName} is active again.
Sorry for the confusion — we’re here to help and will be in touch if next steps are needed.

— ${businessName} Care Team
`;
  const html = `
  <div style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; line-height:1.5;">
    <p>${who}</p>
    <p>A quick correction: your <strong>${inquiryKind ?? "care"
    }</strong> request at <strong>${businessName}</strong> is active again.</p>
    <p>Sorry for the confusion — we’re here to help and will be in touch if next steps are needed.</p>
    <p>— ${businessName} Care Team</p>
  </div>`.trim();
  return { subject, text, html };
}

/* ---------------------------------------------
 * Main Dashboard
 * ------------------------------------------- */
export default function CareProviderDashboardPage() {
  const theme = useTheme();
  const surfaces = theme.customSurfaces || {};
  const chrome = theme.customChrome || {};
  const isProd = process.env.NODE_ENV === "production";
  const [isPageVisible, setIsPageVisible] = React.useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );
  const livePollMs = isPageVisible ? (isProd ? 30000 : 15000) : 0;
  const selectedBusiness = useSelectedBusiness();
  const businessBasic = selectedBusiness?.businessBasic;
  const { reload } = useBusinessFromStore();
  const businessId = useMemo(() => {
    const raw = businessBasic?.id ?? (businessBasic as any)?.businessId;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [businessBasic]);

  const bizName = businessBasic?.name ?? "Care Provider";
  const bizAddress = businessBasic?.address ?? "";
  const businessBookingTypes = selectedBusiness?.businessBookingTypes;
  const { data: agentBookingTypesData } = useQuery(GET_AGENT_BOOKING_TYPES, {
    variables: { businessId: String(businessId), createdByAgentId: null },
    skip: !businessId,
    fetchPolicy: "cache-and-network",
  });
  const { data: bookingProviderConnectionData } = useQuery(
    GET_BOOKING_PROVIDER_CONNECTION,
    {
      variables: { businessId },
      skip: !businessId,
      fetchPolicy: "cache-and-network",
    }
  );
  const publicPath = businessBasic?.url ? `/${(businessBasic as any).url}` : null;
  const manageJobsPath = businessBasic?.url
    ? `/b/${(businessBasic as any).url}/manage/jobs`
    : "/biz/hiring";
  const shareAvailabilityUrl = useMemo(() => {
    const urlSlug = (businessBasic as any)?.url;
    if (!urlSlug) return "";
    const base = typeof window !== "undefined" ? window.location.origin : "";
    return `${base}/${urlSlug}/care`;
  }, [businessBasic]);

  const incomingReferralBookingTypeId = useMemo(() => {
    const directBookingTypes = Array.isArray(agentBookingTypesData?.bookingTypesByBusiness)
      ? agentBookingTypesData.bookingTypesByBusiness
      : [];
    const cachedBookingTypes = Array.isArray(businessBookingTypes)
      ? businessBookingTypes
      : [];
    const bookingTypes = directBookingTypes.length ? directBookingTypes : cachedBookingTypes;
    if (!bookingTypes.length) return null;

    const conciergeCreated = bookingTypes.filter(
      (bookingType: any) => bookingType?.createdByAgentId != null && bookingType?.createdByAgentId !== ""
    );
    if (!conciergeCreated.length) return null;

    const scoreBookingType = (bookingType: any) => {
      const haystack = `${bookingType?.title || ""} ${bookingType?.link || ""}`.toLowerCase();
      let score = 0;
      if (/(tour|visit|walkthrough)/i.test(haystack)) score += 5;
      return score;
    };

    const ranked = [...conciergeCreated].sort((a, b) => scoreBookingType(b) - scoreBookingType(a));
    return ranked[0]?.id ?? null;
  }, [agentBookingTypesData?.bookingTypesByBusiness, businessBookingTypes]);
  const availabilityManagementUrl = useMemo(
    () =>
      buildBookingProviderAvailabilityUrl(
        bookingProviderConnectionData?.getBookingProviderConnection,
        incomingReferralBookingTypeId
      ),
    [
      bookingProviderConnectionData?.getBookingProviderConnection,
      incomingReferralBookingTypeId,
    ]
  );

  const providerEmail =
    (selectedBusiness as any)?.currentUser?.email ||
    (selectedBusiness as any)?.ownerEmail ||
    "";

  const [installmentToPay, setInstallmentToPay] = useState<null | {
    id: string;
    amount: number;
  }>(null);
  const [payInstallment] = useMutation(PAY_REFERRAL_INSTALLMENT, {
    onError: (e) =>
      setToast(e?.message || "Failed to record installment payment."),
  });

  const beginInstallmentPay = (p: {
    installmentId: string;
    amountDue: number;
  }) => {
    setInstallmentToPay({ id: p.installmentId, amount: p.amountDue });
    setPayInvoice({ id: `INST-${p.installmentId}`, amount: p.amountDue });
    setPayOpen(true);
  };

  const handleInstallmentPaid = async (captureId: string) => {
    if (!installmentToPay) return;
    try {
      await payInstallment({
        variables: {
          installmentId: installmentToPay.id,
          amount: Number(installmentToPay.amount),
          paypalCaptureId: captureId,
        },
      });
      setToast("Installment payment recorded.");
      // refresh leads + any child queries
      await refetchLeads();
    } catch (e: any) {
      setToast(
        e?.message || "Payment captured, but installment update failed."
      );
    } finally {
      setPayOpen(false);
      setPayInvoice(null);
      setInstallmentToPay(null);
    }
  };

  // Rooms & beds
  const {
    data: roomsData,
    loading: roomsLoading,
    refetch: refetchRooms,
  } = useQuery(GET_OPEN_BEDS, {
    variables: { businessId: businessId as number },
    skip: !businessId,
    fetchPolicy: "cache-and-network",
  });

  const noRoomsOrBeds = useMemo(() => {
    if (roomsLoading) return false;
    const rooms = roomsData?.getRoomsByBusiness ?? [];
    if (rooms.length === 0) return true;
    return !rooms.some((r: any) => (r.beds ?? []).length > 0);
  }, [roomsData, roomsLoading]);
  const totalRooms = useMemo(
    () => (roomsData?.getRoomsByBusiness ?? []).length,
    [roomsData?.getRoomsByBusiness]
  );
  const openBedsCount = useMemo(
    () =>
      (roomsData?.getRoomsByBusiness ?? []).reduce((sum: number, room: any) => {
        const beds = Array.isArray(room?.beds) ? room.beds : [];
        return (
          sum +
          beds.filter((bed: any) => {
            const occupied = bed?.occupied ?? bed?.isOccupied ?? bed?.isFull;
            return !occupied;
          }).length
        );
      }, 0),
    [roomsData?.getRoomsByBusiness]
  );

  // Leads
  const {
    data: leadData,
    loading: leadLoading,
    error: leadError,
    refetch: refetchLeads,
  } = useQuery(GET_REFERRAL_LEADS, {
    variables: { businessId: Number(businessId), limit: 100, offset: 0 },
    skip: !businessId,
    fetchPolicy: "cache-and-network",
    pollInterval: businessId ? livePollMs : 0,
  });

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  React.useEffect(() => {
    if (!businessId) return undefined;

    const handleWindowRefresh = () => {
      refetchLeads();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetchLeads();
      }
    };

    window.addEventListener("focus", handleWindowRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [businessId, refetchLeads]);

  const leads = useMemo(() => leadData?.referralLeads ?? [], [leadData?.referralLeads]);
  const leadsCount = leads.length;
  const needsActionCount = useMemo(
    () =>
      leads.filter((l: any) =>
        ["NEW", "QUALIFYING", "MATCHING", "TOUR_SCHEDULED", "PENDING_DECISION"].includes(
          String(l?.status || "").toUpperCase()
        )
      ).length,
    [leads]
  );
  const newIncomingCount = useMemo(
    () =>
      leads.filter(
        (l: any) => String(l?.status || "").toUpperCase() === "NEW"
      ).length,
    [leads]
  );
  // Mutations
  const [changeLeadStatus] = useMutation(CHANGE_REFERRAL_LEAD_STATUS);
  const [confirmSettlement] = useMutation(CONFIRM_REQUEST_SETTLEMENT);
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  // Dialog state
  const [vacateOpen, setVacateOpen] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRow, setCancelRow] = useState<any | null>(null);
  const [cancelReason, setCancelReason] = useState("caregiver_cancelled");
  const [cancelAck, setCancelAck] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string>("");

  const [settleOpen, setSettleOpen] = useState(false);
  const [settleLead, setSettleLead] = useState<any>(null);
  const [settlePricingFlags, setSettlePricingFlags] = useState<
    | { provided: boolean; mode: "SELF_SERVE" | "CONCIERGE"; isRush: boolean }
    | undefined
  >(undefined);

  const [pending, setPending] = useState<{
    leadId: string;
    bedIds: string[];
    feePerBed: number;
    total: number;
  } | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState<MiniInvoice | null>(null);

  const [undoInfo, setUndoInfo] = useState<null | {
    leadId: string;
    refEmail?: string | null;
    referrerName?: string | null;
    apologySent: boolean;
  }>(null);

  const [toast, setToast] = useState<string | null>(null);
  const [broadcastInviteCount, setBroadcastInviteCount] = useState(0);
  const [roomsRefresh, setRoomsRefresh] = useState(0);
  const closeToast = () => setToast(null);
  const scrollToIncoming = useCallback(() => {
    const el = document.getElementById("incoming-referrals-panel");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const copyShareAvailabilityLink = useCallback(async () => {
    if (!shareAvailabilityUrl) return;
    try {
      await navigator.clipboard.writeText(shareAvailabilityUrl);
      setToast("Availability link copied.");
    } catch {
      setToast("Could not copy automatically. Please copy the link manually.");
    }
  }, [shareAvailabilityUrl]);

  const onCancelLead = useCallback((lead: any, ev?: React.MouseEvent) => {
    ev?.preventDefault();
    ev?.stopPropagation();
    openCancelDialog(lead);
    console.log("cancel lead", lead?.id);
  }, []);

  const onBeginInstallmentPay = useCallback(
    ({
      installmentId,
      amountDue,
    }: {
      installmentId: string;
      amountDue: number;
    }) => {
      beginInstallmentPay({ installmentId, amountDue });
      console.log("begin pay", installmentId, amountDue);
    },
    []
  );

  // Actions
  const openSettle = (lead: any) => {
    setSettleLead(lead);
    setSettlePricingFlags(getPricingFlagsFromExtra(lead?.extra));
    setSettleOpen(true);
  };
  const closeSettle = () => {
    setSettleOpen(false);
    setSettleLead(null);
  };

  const handleReserved = (p: {
    bedIds: string[];
    feePerBed: number;
    total: number; // full fee (for records/future installments)
    amountDueNow: number; // ✅ charge this now
    plan: "TWO";
  }) => {
    if (!settleLead?.id) return;
    if (!(Number(p.amountDueNow) > 0)) {
      void (async () => {
        try {
          const { data } = await confirmSettlement({
            variables: { id: settleLead.id, paypalCaptureId: "" },
          });
          const ok = data?.confirmRequestSettlement?.success;
          setToast(
            ok
              ? "Settlement recorded."
              : data?.confirmRequestSettlement?.message ||
                  "Failed to confirm settlement."
          );
          reload();
          await refetchLeads();
          await refetchRooms();
        } catch (e: any) {
          setToast(e?.message || "Failed to confirm settlement.");
        }
      })();
      setSettleOpen(false);
      return;
    }
    setPending({
      leadId: settleLead.id,
      bedIds: p.bedIds,
      feePerBed: p.feePerBed,
      total: p.total, // keep full amount in state for records if needed
    });
    setPayInvoice({
      id: `SETTLE-${settleLead.id}-${Date.now()}`,
      amount: p.amountDueNow, // ✅ charge only the first installment now
    });
    setPayOpen(true);
    setSettleOpen(false);
  };

  const handlePaid = async (captureId: string) => {
    try {
      if (!pending) return;
      const { data } = await confirmSettlement({
        variables: { id: pending.leadId, paypalCaptureId: captureId },
      });
      const ok = data?.confirmRequestSettlement?.success;
      setToast(
        ok
          ? "Payment successful. Beds closed."
          : data?.confirmRequestSettlement?.message ||
          "Payment captured, but confirmation failed."
      );
      reload();
      await refetchLeads();
      await refetchRooms();
    } catch (e: any) {
      setToast(e?.message || "Failed to confirm settlement after payment.");
    } finally {
      setPayOpen(false);
      setPayInvoice(null);
      setPending(null);
    }
  };

  const handlePayCancel = () => {
    setToast("Payment canceled. Reservation not confirmed.");
    setPayOpen(false);
    setPayInvoice(null);
    setPending(null);
  };

  const openCancelDialog = useCallback((row: any) => {
    setCancelRow(row);
    setCancelReason("caregiver_cancelled");
    setCancelAck(true);
    setCancelError("");
    setCancelOpen(true);
  }, []);
  const closeCancelDialog = useCallback(() => {
    if (cancelLoading) return;
    setCancelOpen(false);
    setCancelRow(null);
  }, [cancelLoading]);

  const sendApologyEmail = useCallback(
    async (to: string, referrerName: string | null) => {
      const { subject, text, html } = buildCancelApologyEmail({
        businessName: bizName,
        referrerName,
        inquiryKind: "referral",
      });
      await sendEmail({
        variables: {
          from_email: providerEmail || null,
          to,
          subject,
          text,
          html,
        },
      });
    },
    [sendEmail, providerEmail, bizName]
  );

  const sendCorrectionEmail = useCallback(
    async (to: string, referrerName: string | null) => {
      const { subject, text, html } = buildReopenCorrectionEmail({
        businessName: bizName,
        referrerName,
        inquiryKind: "referral",
      });
      await sendEmail({
        variables: {
          from_email: providerEmail || null,
          to,
          subject,
          text,
          html,
        },
      });
    },
    [sendEmail, providerEmail, bizName]
  );

  const handleConfirmCancel = useCallback(async () => {
    if (!businessId || !cancelRow) return;
    if (!cancelAck) {
      setCancelError("Please acknowledge the courtesy email checkbox.");
      return;
    }
    setCancelLoading(true);
    setCancelError("");

    try {
      await changeLeadStatus({
        variables: { id: cancelRow.id, status: "LOST" },
      });

      const toEmail = cancelRow?.referrer?.email || "";
      const refName = cancelRow?.referrer?.contactName || null;
      if (toEmail) await sendApologyEmail(toEmail, refName);

      setCancelLoading(false);
      setCancelOpen(false);

      setUndoInfo({
        leadId: String(cancelRow.id),
        refEmail: toEmail || null,
        referrerName: refName,
        apologySent: !!toEmail,
      });

      setToast("Referrermarked as LOST. Courtesy apology sent.");
      await refetchLeads();
    } catch (e: any) {
      setCancelLoading(false);
      setCancelError(String(e?.message || "Failed to cancel."));
    }
  }, [
    businessId,
    cancelRow,
    cancelAck,
    changeLeadStatus,
    sendApologyEmail,
    refetchLeads,
  ]);
  const photos: any[] = useMemo(() => {
    const fromImages = businessBasic?.images ?? [];
    return fromImages;
  }, [selectedBusiness]);

  // Consider "has image" true if array has at least one item with either a url, path, or id.
  const hasPhotos = useMemo(() => {
    if (!Array.isArray(photos) || photos.length === 0) return false;
    return photos.some((p: any) => {
      const v = typeof p === "string" ? p : (p?.url ?? p?.path ?? p?.id);
      return !!v && String(v).trim().length > 0;
    });
  }, [photos]);

  const hasDescription =
    typeof businessBasic?.description === "string" &&
    businessBasic.description.trim().length > 0;
  const businessImageUrl = useMemo(() => {
    const firstValid = (photos ?? []).find((p: any) => {
      const v = typeof p === "string" ? p : (p?.url ?? p?.path ?? p?.imageUrl ?? p?.src);
      return !!v && String(v).trim().length > 0;
    });
    if (!firstValid) return "";
    return typeof firstValid === "string"
      ? firstValid
      : String(firstValid?.url ?? firstValid?.path ?? firstValid?.imageUrl ?? firstValid?.src ?? "");
  }, [photos]);


  const derivedPricingFlags = {
    feePerBed: settlePricingFlags?.provided
      ? (
        settlePricingFlags.mode === "CONCIERGE"
          ? FALLBACK_MIN_CONCIERGE
          : FALLBACK_MIN_SELF_SERVE
      ) + (settlePricingFlags.isRush ? FALLBACK_RUSH_ADDER : 0)
      : 0,
  };


  const handleUndo = useCallback(async () => {
    if (!businessId || !undoInfo) return;
    try {
      await changeLeadStatus({
        variables: { id: undoInfo.leadId, status: "NEW" },
      });
      if (undoInfo.apologySent && undoInfo.refEmail) {
        await sendCorrectionEmail(
          undoInfo.refEmail,
          undoInfo.referrerName ?? null
        );
      }
      setToast("Reopened the referral.");
      setUndoInfo(null);
      await refetchLeads();
    } catch (e: any) {
      setToast(String(e?.message || "Failed to undo cancel."));
    }
  }, [
    businessId,
    undoInfo,
    changeLeadStatus,
    sendCorrectionEmail,
    refetchLeads,
  ]);


  return (

    <Box
      sx={{
        width: "100%",
        maxWidth: 1800,
        mx: "auto",
        px: { xs: 1.25, sm: 2.5, md: 3.5, lg: 4.5 },
        py: { xs: 2, sm: 3.25, md: 4.25 },
      }}
    >
      <Box
        sx={{
          mb: { xs: 2, sm: 3.25 },
          borderRadius: { xs: 4, sm: 5 },
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2.25, sm: 3.25 },
          background: surfaces.elevatedPanelGradient || surfaces.elevatedPanel || theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${surfaces.border || "rgba(21, 49, 76, 0.18)"}`,
          boxShadow: "0 24px 48px rgba(15, 31, 46, 0.08)",
          position: "relative",
          overflow: "hidden",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              theme.palette.mode === "dark"
                ? "radial-gradient(circle at top right, rgba(96,165,250,0.12), transparent 34%)"
                : "radial-gradient(circle at top right, rgba(191,219,254,0.32), transparent 34%)",
            pointerEvents: "none",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
          spacing={2.5}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Stack spacing={1.2} sx={{ maxWidth: 760 }}>
            <Typography
              variant="overline"
              sx={{ color: alpha(theme.palette.text.primary, 0.7), letterSpacing: "0.14em", fontWeight: 700 }}
            >
              Care Provider Admin
            </Typography>
            <Typography
              variant="h3"
              fontWeight={700}
              sx={{
                lineHeight: 1.08,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                color: theme.palette.text.primary,
              }}
            >
              Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha(theme.palette.text.primary, 0.72),
                maxWidth: 640,
                lineHeight: 1.7,
              }}
            >
              Manage referrals, beds, and profile details from one polished workspace built for quick daily review.
            </Typography>
          </Stack>

          {businessId && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{
                width: { xs: "100%", lg: "auto" },
                "& .MuiButton-root": {
                  minHeight: 44,
                  borderRadius: 999,
                  px: 2.1,
                  textTransform: "none",
                  fontWeight: 600,
                },
              }}
            >
              <Button
                variant="outlined"
                component={RouterLink}
                to="/biz/marketplace"
                sx={{ borderColor: alpha(theme.palette.text.primary, 0.22), color: theme.palette.text.primary }}
              >
                Marketplace
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to={manageJobsPath}
                sx={{ borderColor: alpha(theme.palette.text.primary, 0.22), color: theme.palette.text.primary }}
              >
                Manage Jobs
              </Button>
              <Button
                variant="outlined"
                onClick={() => setVacateOpen(true)}
                disabled={!businessId}
                sx={{ borderColor: alpha(theme.palette.text.primary, 0.22), color: theme.palette.text.primary }}
              >
                Vacate Beds
              </Button>
            </Stack>
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.2}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: { xs: 2, sm: 3 }, position: "relative", zIndex: 1 }}
        >
          <SummaryMetricCard label="Needs Attention" value={needsActionCount} tone="warm" />
          <SummaryMetricCard label="New Referrals" value={newIncomingCount} tone="cool" />
          <SummaryMetricCard label="Open Beds" value={openBedsCount} />
          <SummaryMetricCard label="Rooms" value={totalRooms} />
        </Stack>
      </Box>

      {!businessId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No business selected. Please choose a business first.
        </Alert>
      )}
      {Boolean(businessId) && needsActionCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: { xs: 1.5, sm: 2 } }}
          action={
            <Button size="small" variant="contained" onClick={scrollToIncoming}>
              Review now
            </Button>
          }
        >
          You have {needsActionCount} incoming referral
          {needsActionCount === 1 ? "" : "s"} needing attention
          {newIncomingCount > 0
            ? ` (${newIncomingCount} new).`
            : "."}
        </Alert>
      )}

      {/* ✅ NEW — If no photos, gently prompt the provider to add them */}
      {Boolean(businessId) && !hasPhotos && (
        <Alert
          severity="info"
          sx={{ mb: { xs: 1.5, sm: 2 } }}
          action={
            <Button
              variant="contained"
              size="small"
              component={RouterLink}
              to="/biz/business/2"         // image
              state={{ businessId }}
            >
              Add photos
            </Button>
          }
        >
          Your profile doesn’t have any photos yet. Adding clear pictures of rooms,
          common areas, bathrooms, accessible equipment, and the exterior helps
          referrers and families trust your home and decide faster.
        </Alert>
      )}
      {Boolean(businessId) && !hasDescription && (
        <Alert
          severity="info"
          sx={{ mb: { xs: 1.5, sm: 2 } }}
          action={
            <Button
              variant="contained"
              size="small"
              component={RouterLink}
              to="/biz/business/0"              // Business Basic tab
              state={{ businessId, openTab: 0 }}// optional hint for your tab sync
            >
              Add description
            </Button>
          }
        >
          Your profile doesn’t have a description yet. Add a short overview of your
          home (care levels, specialties, amenities, neighborhood) so referrers and
          families can quickly determine fit.
        </Alert>
      )}
      <Grid container spacing={{ xs: 1.5, sm: 2, lg: 2.5 }}>
        <Grid item xs={12} md={4} lg={3}>
          <DashboardPanel
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={businessImageUrl || undefined}
                  alt={bizName}
                  sx={{
                    width: 38,
                    height: 38,
                    border: "2px solid rgba(20,53,78,0.08)",
                    boxShadow: "0 10px 24px rgba(10, 30, 48, 0.1)",
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={700}>
                  {bizName}
                </Typography>
              </Stack>
            }
            actions={
              publicPath ? (
                <Button
                  component={RouterLink}
                  to={publicPath}
                  target="_blank"
                  rel="noopener"
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  sx={{
                    textTransform: "none",
                    px: 1.2,
                    minWidth: 0,
                    borderRadius: 999,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.05),
                  }}
                >
                  View
                </Button>
              ) : null
            }
          >
            {bizAddress ? (
              <Typography variant="body2" color="text.secondary">
                {bizAddress}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No business address on file.
              </Typography>
            )}
          </DashboardPanel>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <DashboardPanel
            title="Share Beds Availability"
            actions={
              <Button
                component="a"
                href={shareAvailabilityUrl || undefined}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                disabled={!shareAvailabilityUrl}
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 1.25,
                }}
              >
                Open link
              </Button>
            }
          >
            <TextField
              fullWidth
              size="small"
              value={shareAvailabilityUrl}
              placeholder="No public URL found for this business yet."
              InputProps={{
                readOnly: true,
                sx: {
                  borderRadius: 3,
                  bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.06) : "rgba(242,246,250,0.8)",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Copy availability link"
                      edge="end"
                      onClick={copyShareAvailabilityLink}
                      disabled={!shareAvailabilityUrl}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DashboardPanel>
        </Grid>

        <Grid item xs={12}>
          <DashboardPanel
            title="Care Settings"
            collapsible
            defaultExpanded={false}
            fillHeight={false}
            actions={
              noRoomsOrBeds ? (
                <Chip
                  label="No rooms/beds yet"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              ) : null
            }
          >
            <CareProvider isDisabled={false} />
          </DashboardPanel>
        </Grid>

        <Grid item xs={12}>
          <DashboardPanel
            title="Rooms & Beds Availability"
            collapsible
            defaultExpanded={false}
            fillHeight={false}
          >
            <RoomsBedsMetaSection
              key={`rooms-${businessId}-${roomsRefresh}`}
              title="Rooms & Beds Availability"
              defaultExpanded
              panelMode
            />
          </DashboardPanel>
        </Grid>

        <Grid item xs={12} id="incoming-referrals-panel">
          <DashboardPanel
            title="Incoming Referrals"
            collapsible
            defaultExpanded={false}
            fillHeight={false}
            actions={
              <Stack direction="row" spacing={1}>
                <Tooltip
                  title={
                    availabilityManagementUrl
                      ? "Open the connected booking provider and update availability."
                      : "Configure a booking provider from Schedule before managing availability."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      href={availabilityManagementUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!availabilityManagementUrl}
                      sx={{ borderRadius: 999, textTransform: "none" }}
                    >
                      Update Tour Availability
                    </Button>
                  </span>
                </Tooltip>
                <Chip
                  size="small"
                  color={leadsCount + broadcastInviteCount > 0 ? "primary" : "default"}
                  variant={leadsCount + broadcastInviteCount > 0 ? "filled" : "outlined"}
                  sx={{ borderRadius: 999, fontWeight: 600 }}
                  label={
                    leadsCount + broadcastInviteCount > 0
                      ? `${leadsCount + broadcastInviteCount} total`
                      : "0 total"
                  }
                />
                <Chip
                  size="small"
                  color={newIncomingCount + broadcastInviteCount > 0 ? "warning" : "default"}
                  variant={newIncomingCount + broadcastInviteCount > 0 ? "filled" : "outlined"}
                  sx={{ borderRadius: 999, fontWeight: 600 }}
                  label={
                    newIncomingCount + broadcastInviteCount > 0
                      ? `${newIncomingCount + broadcastInviteCount} new`
                      : "0 new"
                  }
                />
              </Stack>
            }
          >
            <IncomingReferralsSection
              availabilityManagementUrl={availabilityManagementUrl}
              leads={leads}
              leadsCount={leadsCount}
              leadLoading={leadLoading}
              leadError={leadError}
              businessId={businessId}
              businessName={bizName}
              refetchLeads={refetchLeads}
              onOpenSettle={openSettle}
              onCancelLead={onCancelLead}
              onBeginInstallmentPay={beginInstallmentPay}
              cancelLoading={false}
              onBroadcastInviteCountChange={setBroadcastInviteCount}
              panelMode
            />
          </DashboardPanel>
        </Grid>
      </Grid>

      {/* Vacate Beds dialog */}
      {businessId && (
        <VacateBedsDialog
          open={vacateOpen}
          businessId={businessId}
          onClose={() => setVacateOpen(false)}
          onDone={async (n: number) => {
            setToast(`${n} bed(s) marked free.`);
            await refetchRooms();
            setRoomsRefresh((t) => t + 1);
          }}
        />
      )}

      {/* Settle dialog */}
      {businessId && settleLead && (
        <BedMultiSettleDialog
          open={settleOpen}
          businessId={businessId}
          lead={settleLead}
          pricingFlags={derivedPricingFlags}
          onClose={closeSettle}
          onReserved={handleReserved}
        />
      )}

      {/* PayPal dialog */}
      <PayInvoiceDialog
        open={payOpen}
        invoice={payInvoice}
        onClose={() => {
          setPayOpen(false);
          setPayInvoice(null);
          setPending(null);
          setInstallmentToPay(null);
        }}
        onPaid={(captureId: string) => {
          if (pending) return handlePaid(captureId);
          if (installmentToPay) return handleInstallmentPaid(captureId);
        }}
        onCancel={handlePayCancel}
      />

      {/* Cancel & Notify dialog */}
      <Dialog
        open={cancelOpen}
        onClose={cancelLoading ? undefined : closeCancelDialog}
        fullWidth
        maxWidth="sm"
        keepMounted
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Cancel referral & send courtesy notice
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              We’ll send a short “sorry” message to the referrer to confirm the
              referral was <strong>not placed</strong> at{" "}
              <strong>{bizName}</strong>.
            </Typography>

            <TextField
              select
              fullWidth
              label="Reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(String(e.target.value))}
            >
              <MenuItem value="caregiver_cancelled">
                Cancelled by careprovider(no placement)
              </MenuItem>
              <MenuItem value="capacity_full">Capacity full</MenuItem>
              <MenuItem value="care_needs_mismatch">
                Care needs mismatch
              </MenuItem>
              <MenuItem value="insurance_mismatch">
                Insurance / payer mismatch
              </MenuItem>
              <MenuItem value="out_of_service_area">
                Out of service area
              </MenuItem>
              <MenuItem value="duplicate">Duplicate / spam</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Checkbox
                  checked={cancelAck}
                  onChange={(e) => setCancelAck(e.target.checked)}
                />
              }
              label="I understand a courtesy apology will be sent to the referrer."
            />

            {cancelError ? <Alert severity="error">{cancelError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={cancelLoading}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancel}
            disabled={!cancelAck || cancelLoading}
          >
            {cancelLoading ? "Cancelling…" : "Cancel & Send Apology"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast + Undo */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        action={
          undoInfo ? (
            <Button
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleUndo();
              }}
            >
              Undo
            </Button>
          ) : null
        }
      >
        <MUIAlert
          onClose={closeToast}
          severity="success"
          variant="filled"
          elevation={6}
        >
          {toast}
        </MUIAlert>
      </Snackbar>
    </Box>
  );
}
